/**
 * React provider that bridges the framework-agnostic state machine
 * (`auth/state.ts`) into Mantine + React Router consumers via
 * `useSyncExternalStore`. Also wires:
 *
 * - **Silent refresh:** a `setTimeout` 5 min before token expiry calls
 *   `refreshInBackground()`. Re-scheduled on every token change.
 * - **Refresh while active:** every minute, if the user did something in that minute (in
 *   any portal tab, see `auth/activity.ts`) and the token is older than 10 min, refresh.
 *   This keeps the BFF's refresh cookie inside its 30 min idle bound, so working in the
 *   portal does not by itself end the session (docs#239 finding 41).
 * - **Session-ending warning:** when a background refresh is refused the token still works
 *   until it expires. The provider says so, with the time left and a sign-in button, retries
 *   every minute, and ends the session at expiry instead of on the next click.
 * - **Idle timeout:** no activity in any portal tab for 25 min → warn modal, 30 min →
 *   logout. Per-tab tracking let an idle tab sign out a doctor working in another one.
 *   The modal is rendered by the provider so every authenticated surface inherits it.
 *
 * The `useIAMAuth()` hook in `auth/iam.ts` re-exports the snapshot in the
 * shape the chip plan locked (`isReady`, `isAuthenticated`, `permissions`,
 * `signIn`, `signOut`).
 */

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Affix, Button, Group, Modal, Stack, Text, Title } from "@mantine/core";

import { ACTIVITY_EVENTS, lastActivityAt, recordActivity } from "@/auth/activity";
import { buildLoginRedirect, buildLogoutRedirect, refreshInBackground } from "@/auth/flow";
import { getSnapshot, setExpired, subscribe, type AuthSnapshot } from "@/auth/state";
import { t } from "@/i18n";

/**
 * Refresh this long before the access token expires. Five minutes, not one, so that when
 * IAM refuses the refresh the user still has time to read the warning and sign in again.
 */
export const REFRESH_LEAD_TIME_SEC = 5 * 60;
/** The lead never exceeds half the token's lifetime, or a short token would refresh in a loop. */
const MIN_REFRESH_LEAD_SEC = 30;
/**
 * While the user is active, refresh once the token is this old. It must stay well under
 * the BFF's idle bound (`auth_refresh_idle_seconds`, 1800 s by default in onboarding-api),
 * or the BFF refuses the refresh of an active user.
 */
export const ACTIVE_REFRESH_AFTER_MS = 10 * 60 * 1000;
/** How often the provider checks for activity, and retries a refused refresh. */
export const SESSION_TICK_MS = 60 * 1000;
const IDLE_WARN_MS = 25 * 60 * 1000;
const IDLE_LOGOUT_MS = 30 * 60 * 1000;

/** How long before expiry to refresh: 5 min, or half the token's lifetime if shorter. */
export function refreshLeadSec(expiresAtSec: number, issuedAtSec: number | undefined): number {
  if (issuedAtSec === undefined || issuedAtSec >= expiresAtSec) return REFRESH_LEAD_TIME_SEC;
  const half = Math.floor((expiresAtSec - issuedAtSec) / 2);
  return Math.min(REFRESH_LEAD_TIME_SEC, Math.max(MIN_REFRESH_LEAD_SEC, half));
}

export function useAuthSnapshot(): AuthSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

interface AuthProviderProps {
  children: React.ReactNode;
  /**
   * Test seam — override the default Mantine idle thresholds. Production
   * code should never pass these; defaults are 25min warn / 30min logout
   * per the chip plan.
   */
  idleWarnMs?: number;
  idleLogoutMs?: number;
}

export function AuthProvider({
  children,
  idleWarnMs = IDLE_WARN_MS,
  idleLogoutMs = IDLE_LOGOUT_MS,
}: AuthProviderProps): JSX.Element {
  const snapshot = useAuthSnapshot();
  const navigate = useNavigate();
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Silent refresh: re-arm a single setTimeout per token. ----
  useEffect(() => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (snapshot.status !== "authenticated" || snapshot.tokens === null) {
      return;
    }
    const nowSec = Math.floor(Date.now() / 1000);
    const lead = refreshLeadSec(
      snapshot.tokens.expiresAtSec,
      snapshot.claims?.iat ?? snapshot.tokens.obtainedAtSec,
    );
    const fireInMs = Math.max(0, (snapshot.tokens.expiresAtSec - lead - nowSec) * 1000);
    refreshTimerRef.current = setTimeout(() => {
      void refreshInBackground();
    }, fireInMs);
    return () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [snapshot.status, snapshot.tokens, snapshot.claims]);

  // ---- Activity, in this tab, shared with the others. ----
  useEffect(() => {
    const touch = (): void => {
      recordActivity();
    };
    for (const event of ACTIVITY_EVENTS) document.addEventListener(event, touch, { passive: true });
    return () => {
      for (const event of ACTIVITY_EVENTS) document.removeEventListener(event, touch);
    };
  }, []);

  // ---- Expired transition: kick the user back to /login. ----
  useEffect(() => {
    if (snapshot.status === "expired") {
      navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`);
    }
  }, [snapshot.status, navigate]);

  const authenticated = snapshot.status === "authenticated" && snapshot.tokens !== null;
  return (
    <>
      {authenticated ? <IdleTimeoutWatcher warnMs={idleWarnMs} logoutMs={idleLogoutMs} /> : null}
      {authenticated && snapshot.tokens !== null ? (
        <SessionKeeper tokens={snapshot.tokens} sessionEnding={snapshot.sessionEnding} />
      ) : null}
      {children}
    </>
  );
}

interface SessionKeeperProps {
  tokens: NonNullable<AuthSnapshot["tokens"]>;
  sessionEnding: boolean;
}

/**
 * Keeps an active user's session going, and warns before it ends when it cannot.
 *
 * Once a minute: if the session is flagged as ending, retry the refresh (a passing IAM
 * outage clears it). Otherwise, if the user did anything in the last minute and the token
 * is at least `ACTIVE_REFRESH_AFTER_MS` old, refresh. The refresh re-sets the httpOnly
 * cookie, whose Max-Age is the idle window, so the cookie only runs out when the user has
 * stopped working, and a reload in the middle of work restores without IAM.
 */
function SessionKeeper({ tokens, sessionEnding }: SessionKeeperProps): JSX.Element {
  const navigate = useNavigate();
  const tokenSinceRef = useRef<number>(Date.now());
  // Bumped by the timer so the minutes left re-render once a minute.
  const [, setTick] = useState<number>(0);

  useEffect(() => {
    tokenSinceRef.current = Date.now();
  }, [tokens]);

  // Read by the timer, so the one-minute rhythm is not restarted when the flag flips.
  const sessionEndingRef = useRef<boolean>(sessionEnding);
  sessionEndingRef.current = sessionEnding;

  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      setTick(now);
      if (sessionEndingRef.current) {
        void refreshInBackground();
        return;
      }
      const activeThisTick = now - lastActivityAt(now) < SESSION_TICK_MS;
      if (activeThisTick && now - tokenSinceRef.current >= ACTIVE_REFRESH_AFTER_MS) {
        void refreshInBackground();
      }
    }, SESSION_TICK_MS);
    return () => {
      clearInterval(id);
    };
  }, []);

  // A refused refresh leaves a token with a known end. End the session then, rather than
  // on whichever click first meets a 401.
  useEffect(() => {
    if (!sessionEnding) return undefined;
    const msLeft = tokens.expiresAtSec * 1000 - Date.now();
    const id = setTimeout(
      () => {
        setExpired();
      },
      Math.max(0, msLeft),
    );
    return () => {
      clearTimeout(id);
    };
  }, [sessionEnding, tokens]);

  if (!sessionEnding) {
    return <></>;
  }
  const minutesLeft = Math.max(1, Math.ceil((tokens.expiresAtSec * 1000 - Date.now()) / 60_000));
  const next = window.location.pathname + window.location.search;
  return (
    <Affix position={{ top: 16, left: 16, right: 16 }} zIndex={400}>
      <Alert
        color="yellow"
        title={t("session.ending_title")}
        role="alert"
        data-testid="session-ending"
        maw={640}
        mx="auto"
      >
        <Stack gap="sm">
          <Text size="sm">
            {t("session.ending_body").replace("{minutes}", String(minutesLeft))}
          </Text>
          <Group justify="flex-end">
            <Button
              size="xs"
              onClick={() => {
                navigate(`/login?next=${encodeURIComponent(next)}`);
              }}
            >
              {t("session.sign_in_again")}
            </Button>
          </Group>
        </Stack>
      </Alert>
    </Affix>
  );
}

interface IdleTimeoutWatcherProps {
  warnMs: number;
  logoutMs: number;
}

function IdleTimeoutWatcher({ warnMs, logoutMs }: IdleTimeoutWatcherProps): JSX.Element {
  // Idle means no input in ANY portal tab (auth/activity.ts). Polled, because the newest
  // input may come from another tab, which fires no event here. Signing in counts as
  // activity, so a fresh session never starts out idle.
  const [idle, setIdle] = useState<boolean>(false);

  useEffect(() => {
    recordActivity();
    const pollMs = Math.max(250, Math.min(15_000, Math.floor(warnMs / 4)));
    const id = setInterval(() => {
      const idleFor = Date.now() - lastActivityAt();
      if (idleFor >= logoutMs) {
        clearInterval(id);
        window.location.assign(buildLogoutRedirect());
        return;
      }
      setIdle(idleFor >= warnMs);
    }, pollMs);
    return () => {
      clearInterval(id);
    };
  }, [warnMs, logoutMs]);

  if (!idle) {
    return <></>;
  }
  return (
    <Modal
      opened
      onClose={() => {
        /* user must click Stay or Sign out — no backdrop dismissal */
      }}
      withCloseButton={false}
      title={<Title order={3}>Are you still there?</Title>}
      centered
    >
      <Stack gap="md">
        <Text>
          You have been inactive for {Math.floor(warnMs / 60_000)} minutes. For your security you
          will be signed out in {Math.floor((logoutMs - warnMs) / 60_000)} minutes.
        </Text>
        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={() => {
              window.location.assign(buildLogoutRedirect());
            }}
          >
            Sign out now
          </Button>
          <Button
            onClick={() => {
              recordActivity();
              setIdle(false);
            }}
          >
            Stay signed in
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Imperative login init — wraps `buildLoginRedirect` for the `signIn()` API. */
export async function signIn(next = "/"): Promise<void> {
  const url = await buildLoginRedirect(next);
  window.location.assign(url);
}

/** Imperative logout — clears state and redirects to IAM end-session. */
export async function signOut(): Promise<void> {
  window.location.assign(buildLogoutRedirect());
}
